/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { EmployeeModel } from '../../../../models/employee.model';
import { TaskModel } from '../../../../models/task.model';
import { BarChartDatasetsModel, BarChartModel } from '../../../../models/timetracking.model';
import { WorkflowModel } from '../../../../models/workflow.model';
import { EmployeeService } from '../../../../services/employee.service';
import { TaskService } from '../../../../services/task.service';
import { TimeTrackingService } from '../../../../services/time-tracking.service';
import { WorkflowService } from '../../../../services/workflow.service';

@Component({
  selector: 'app-task-dashboard',
  templateUrl: './task-dashboard.component.html',
  styleUrls: ['./task-dashboard.component.scss'],
  standalone: false
})
export class TaskDashboardComponent implements OnInit {
  // page properties
  pageTitle: string = '';
  taskName: string = '';
  // cards
  startDate: string = '';
  endDate: string = '';
  medianTimeForAllEmployees: number = 0;
  medianTimeForThisEmployee: number = 0;
  //Bar chart properties
  loading: boolean = true;
  barChartDataObject!: BarChartModel;

  workflowChangedSubscription: Subscription = new Subscription();
  tasksChangedSubscription: Subscription = new Subscription();

  taskId: string = '';
  employeeId: string = '';
  workflowId: string = '';
  tasks: TaskModel[] = [];
  workflow!: WorkflowModel;
  taskData!: TaskModel;
  employeeData!: EmployeeModel;
  workflowTasks: TaskModel[] = [];

  taskCheckPoint: {
    name: string;
    timeTaken: number;
  }[] = [];

  checkPointList: {
    name: string;
    timeTaken: number[];
    medianTime: number;
  }[] = [];

  allCheckPoints: {
    name: string;
    timeTaken: number[];
    medianTime: number;
  }[] = [];

  constructor(
    private route: ActivatedRoute,
    private workflowService: WorkflowService,
    private employeeService: EmployeeService,
    private taskService: TaskService,
    private timeTrackingService: TimeTrackingService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(async (params) => {
      this.loading = true;
      this.tasks = [];
      this.workflowTasks = [];
      this.taskId = params.taskId;
      this.employeeId = params.employeeId;
      this.workflowId = params.workflowId;
      this.workflow = await this.workflowService.getWorkflow(this.workflowId);
      this.employeeData = await this.employeeService.getEmployee(this.employeeId);
      this.taskData = await this.taskService.getTask(this.taskId);
      this.tasks = await this.getTasks(this.workflowId, params.fromDate, params.toDate);
      this.tasks.forEach((task: TaskModel) => {
        if (task.workflowId == this.workflowId) {
          if (!this.workflowTasks.includes(task)) {
            this.workflowTasks.push(task);
          }
        }
      });
      this.loading = false;

      if (this.workflow) {
        this.allCheckPoints = [];
        this.checkPointList = [];
        this.taskCheckPoint = [];
        this.medianTimeForAllEmployees = 0;
        this.pageTitle = this.taskData.name;

        let employeeTasks: TaskModel[] = this.workflowTasks.filter((task: TaskModel) =>
          task.executedBy.includes(this.employeeData.employeeId)
        );

        this.fillTaskCheckpoints();

        this.fillEmployeeCheckpoints(employeeTasks);

        this.fillAllEmployeeCheckpoints();

        //Bar chart
        let labelsArray = this.checkPointList.map((x) => x.name);
        this.barChartDataObject = this.timeTrackingService.createBarChart(
          labelsArray,
          this.getBarChartDatasets()
        );
      }
      this.cdr.detectChanges();
    });
  }
  getBarChartDatasets(): BarChartDatasetsModel[] {
    let barChartArray1 = this.taskCheckPoint.map((x) => x.timeTaken.toPrecision(2));
    let barChartArray2 = this.checkPointList.map((x) => x.medianTime.toPrecision(2));
    let barChartArray3 = this.allCheckPoints.map((x) => x.medianTime.toPrecision(2));
    let barChartDatasets: BarChartDatasetsModel[] = [
      {
        label: $localize`:@@timeTaken:Time Taken`,
        backgroundColor: '#c26c6a',
        data: barChartArray1,
        maxBarThickness: 100
      },
      {
        label: $localize`:@@medianTimeForThisEmployee:Median Time For This Employee`,
        backgroundColor: '#4A7563',
        data: barChartArray2,
        maxBarThickness: 100
      },
      {
        label: $localize`:@@medianTimeForAllEmployees:Median Time For All Employees`,
        backgroundColor: '#d89849',
        data: barChartArray3,
        maxBarThickness: 100
      }
    ];
    return barChartDatasets;
  }
  ngOnDestroy() {
    this.workflowChangedSubscription.unsubscribe();
    this.tasksChangedSubscription.unsubscribe();
  }

  async getTasks(workflowId: string, filterStart: string, filterEnd: string): Promise<TaskModel[]> {
    return (
      await this.taskService.selectTasks(
        {
          search_criteria: {
            status: { $eq: 'CLOSED' }
          },
          skip: 0,
          limit: 0,
          sort: 'name',
          select: 'task_id name assigned_to workflow_id executed_by claimed_by time_tracking'
        },
        filterStart,
        filterEnd
      )
    ).body;
  }

  fillTaskCheckpoints() {
    let start: Date;
    let end: Date;
    for (let i = 0; i < this.taskData.time_tracking?.length!; i++) {
      let check = this.taskData.time_tracking![i];
      if (check.name === 'start') {
        start = check.start;
        this.startDate = start.toString();
      } else if (check.name === 'end') {
        end = check.start;
        this.endDate = end.toString();
      } else {
        let curr = check.start;
        let next = end!;
        if (i + 1 < this.taskData.time_tracking?.length!) {
          next = this.taskData.time_tracking![i + 1].start;
          let span = (Date.parse(next.toString()) - Date.parse(curr.toString())) / 3600000;
          if (!this.taskCheckPoint.map((x) => x.name).includes(check.name)) {
            let point = {
              name: check.name,
              timeTaken: span
            };
            this.taskCheckPoint.push(point);
          }
        } else {
          end = check.start;
          this.endDate = end.toString();
        }
      }
    }

    for (let i = 0; i < this.taskCheckPoint.length; i++) {
      let point: {
        name: string;
        timeTaken: number[];
        medianTime: number;
      } = {
        name: this.taskCheckPoint[i].name,
        timeTaken: [],
        medianTime: 0
      };
      this.checkPointList.push(point);
    }
  }

  fillEmployeeCheckpoints(employeeTasks: TaskModel[]) {
    for (let task of employeeTasks) {
      if (task.time_tracking && task.time_tracking.length >= 2) {
        let start: Date;
        let end: Date;
        for (let i = 0; i < task.time_tracking?.length!; i++) {
          let check = task.time_tracking![i];
          if (check.name === 'start') {
            start = check.start;
          } else if (check.name === 'end') {
            end = check.start;
          } else {
            let curr = check.start;
            let next = end!;
            if (i + 1 < task.time_tracking?.length!) {
              next = task.time_tracking![i + 1].start;
              let span = (Date.parse(next.toString()) - Date.parse(curr.toString())) / 3600000;
              if (this.checkPointList.map((x) => x.name).includes(check.name)) {
                let obj = this.checkPointList.find((x) => x.name == check.name);
                let ind = this.checkPointList.indexOf(obj!);
                this.checkPointList[ind].timeTaken.push(span);
              }
            } else {
              end = check.start;
            }
          }
        }
      }
    }

    for (let i = 0; i < this.checkPointList.length; i++) {
      this.checkPointList[i].medianTime = this.timeTrackingService.median(
        this.checkPointList[i].timeTaken
      );
      let point: {
        name: string;
        timeTaken: number[];
        medianTime: number;
      } = {
        name: this.checkPointList[i].name,
        timeTaken: [],
        medianTime: 0
      };
      this.allCheckPoints.push(point);
    }

    // for dashboard card
    this.checkPointList
      .map((x) => x.medianTime)
      .forEach((x) => (this.medianTimeForThisEmployee += x));
  }

  fillAllEmployeeCheckpoints() {
    for (let task of this.workflowTasks) {
      if (task.time_tracking && task.time_tracking.length >= 2) {
        let start: Date;
        let end: Date;
        for (let i = 0; i < task.time_tracking?.length!; i++) {
          let check = task.time_tracking![i];
          if (check.name === 'start') {
            start = check.start;
          } else if (check.name === 'end') {
            end = check.start;
          } else {
            let curr = check.start;
            let next = end!;
            if (i + 1 < task.time_tracking?.length!) {
              next = task.time_tracking![i + 1].start;
              let span = (Date.parse(next.toString()) - Date.parse(curr.toString())) / 3600000;
              if (this.allCheckPoints.map((x) => x.name).includes(check.name)) {
                let obj = this.allCheckPoints.find((x) => x.name == check.name);
                let ind = this.allCheckPoints.indexOf(obj!);
                this.allCheckPoints[ind].timeTaken.push(span);
              }
            } else {
              end = check.start;
            }
          }
        }
      }
    }

    for (let i = 0; i < this.allCheckPoints.length; i++) {
      this.allCheckPoints[i].medianTime = this.timeTrackingService.median(
        this.allCheckPoints[i].timeTaken
      );
    }

    // for dashboard card
    this.allCheckPoints
      .map((x) => x.medianTime)
      .forEach((x) => (this.medianTimeForAllEmployees += x));
  }
}
